import type { ClaudeCodePermissionMode } from './claude-code-permission-mode';
import { isClaudeCodePermissionMode } from './claude-code-permission-mode';
import type { CodexPermissionMode } from './codex-permission-mode';
import {
  normalizeCodexPermissionModeInput,
} from './codex-permission-mode';
import type { CliPermissionMode } from './cli-permission-mode';
import {
  CLI_PERMISSION_MODE_DANGEROUS,
  CLI_PERMISSION_MODE_DEFAULT,
  normalizeCliPermissionModeInput,
} from './cli-permission-mode';

/** Key inside {@link AgentConfig} for Claude Code permission mode (ACP `session/set_mode` modeId with claude-agent-acp). */
export const AGENT_CONFIG_CLAUDE_PERMISSION_MODE_KEY = 'claude_permission_mode' as const;

/** Key inside {@link AgentConfig} for bridge CLI permission handling (auto-approve ACP permission prompts). */
export const AGENT_CONFIG_CLI_PERMISSION_MODE_KEY = 'cli_permission_mode' as const;

/** Key inside {@link AgentConfig} for Codex CLI sandbox/approval mode flags. */
export const AGENT_CONFIG_CODEX_PERMISSION_MODE_KEY = 'codex_permission_mode' as const;

/** Key inside {@link AgentConfig} for the selected agent model id (`session/set_config_option` when exposed; omit for Default). */
export const AGENT_CONFIG_AGENT_MODEL_KEY = 'agent_model' as const;

/** Per-turn JSON object on `session_turns.agent_config` (agent-specific). */
export type AgentConfig = Record<string, unknown>;

export function buildAgentConfigForClaudePermissionMode(mode: ClaudeCodePermissionMode): AgentConfig {
  return { [AGENT_CONFIG_CLAUDE_PERMISSION_MODE_KEY]: mode };
}

export function parseAgentConfigJson(raw: string | null | undefined): AgentConfig | null {
  if (raw == null || typeof raw !== 'string' || raw.trim() === '') return null;
  try {
    const o = JSON.parse(raw) as unknown;
    return o && typeof o === 'object' && !Array.isArray(o) ? (o as AgentConfig) : null;
  } catch {
    return null;
  }
}

export function stringifyAgentConfig(config: AgentConfig | null): string | null {
  if (config == null || Object.keys(config).length === 0) return null;
  try {
    return JSON.stringify(config);
  } catch {
    return null;
  }
}

/** Reads Claude permission mode from agent config, if valid. */
export function getClaudePermissionModeFromAgentConfig(config: AgentConfig | null): ClaudeCodePermissionMode | null {
  if (!config) return null;
  const raw = config[AGENT_CONFIG_CLAUDE_PERMISSION_MODE_KEY];
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  return isClaudeCodePermissionMode(t) ? t : null;
}

/** Reads CLI bridge permission mode from agent config (`default` when absent or unknown). */
export function getCliPermissionModeFromAgentConfig(config: AgentConfig | null): CliPermissionMode {
  if (!config) return CLI_PERMISSION_MODE_DEFAULT;
  return normalizeCliPermissionModeInput(config[AGENT_CONFIG_CLI_PERMISSION_MODE_KEY]);
}

/** Reads Codex sandbox/approval preset from agent config (`auto` when absent or unknown). */
export function getCodexPermissionModeFromAgentConfig(config: AgentConfig | null): CodexPermissionMode | null {
  if (!config) return null;
  return normalizeCodexPermissionModeInput(config[AGENT_CONFIG_CODEX_PERMISSION_MODE_KEY]);
}

/**
 * Merges Codex sandbox/approval mode into an existing per-turn config.
 * Omits the key when no ACP mode id was selected.
 */
export function mergeCodexPermissionModeIntoAgentConfig(
  base: Record<string, unknown> | null,
  codexRaw: unknown,
): Record<string, unknown> | null {
  const mode = normalizeCodexPermissionModeInput(codexRaw);
  const merged: Record<string, unknown> = { ...(base ?? {}) };
  if (mode == null) {
    delete merged[AGENT_CONFIG_CODEX_PERMISSION_MODE_KEY];
  } else {
    merged[AGENT_CONFIG_CODEX_PERMISSION_MODE_KEY] = mode;
  }
  return Object.keys(merged).length > 0 ? merged : null;
}

/**
 * Merges {@link AGENT_CONFIG_CLI_PERMISSION_MODE_KEY} into an existing per-turn config.
 * Omits the key when mode is {@link CLI_PERMISSION_MODE_DEFAULT} so payloads stay small.
 */
export function mergeCliPermissionModeIntoAgentConfig(
  base: Record<string, unknown> | null,
  cliRaw: unknown,
): Record<string, unknown> | null {
  const mode = normalizeCliPermissionModeInput(cliRaw);
  const merged: Record<string, unknown> = { ...(base ?? {}) };
  if (mode === CLI_PERMISSION_MODE_DANGEROUS) {
    merged[AGENT_CONFIG_CLI_PERMISSION_MODE_KEY] = CLI_PERMISSION_MODE_DANGEROUS;
  } else {
    delete merged[AGENT_CONFIG_CLI_PERMISSION_MODE_KEY];
  }
  return Object.keys(merged).length > 0 ? merged : null;
}

/** Merges selected agent model into per-turn config; omits the key when absent or empty (Default). */
export function mergeAgentModelIntoAgentConfig(
  base: Record<string, unknown> | null,
  agentModel: unknown,
): Record<string, unknown> | null {
  const v = typeof agentModel === 'string' ? agentModel.trim() : '';
  const merged: Record<string, unknown> = { ...(base ?? {}) };
  if (v !== '') merged[AGENT_CONFIG_AGENT_MODEL_KEY] = v;
  else delete merged[AGENT_CONFIG_AGENT_MODEL_KEY];
  return Object.keys(merged).length > 0 ? merged : null;
}

export function getAgentModelFromAgentConfig(config: AgentConfig | null): string | null {
  if (!config) return null;
  const cur = config[AGENT_CONFIG_AGENT_MODEL_KEY];
  if (typeof cur !== 'string') return null;
  const t = cur.trim();
  return t !== '' ? t : null;
}
